import { cn } from '@nesso/ui/lib/utils';

import { type ChiefComplaintInput, chiefComplaintSchema } from '@/entities/session-note';
import { ToggleableViewCheckboxList } from '@/shared/ui/checkbox-list/toggleable-view-checkbox-list';
import { MultiSelect } from '@/shared/ui/common/multi-select';
import { NoteTextarea } from '@/shared/ui/common/note-textarea';
import { FieldSection } from '@/shared/ui/forms/field-section';

type ChiefComplaintContent = ChiefComplaintInput & {
    relatedToOther?: string;
};

type Props = {
    content: ChiefComplaintContent;
    onChange: (content: ChiefComplaintContent) => void;
};

const EMOTION_OPTIONS = chiefComplaintSchema.shape.emotion
    .unwrap()
    .element.options.map((label) => ({ value: label, label }));

const DISCUSSED_TOPIC_OPTIONS = chiefComplaintSchema.shape.relatedTo
    .unwrap()
    .element.options.map((label) => ({ value: label, label }));

export function NessoChiefComplaint({ content, onChange }: Props) {
    return (
        <FieldSection title="Chief Complaint" className="gap-10">
            <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end sm:gap-2">
                    <span className="text-base leading-6">Client reported feeling</span>
                    <div className="w-full sm:w-[20.3125rem]">
                        <MultiSelect
                            options={EMOTION_OPTIONS}
                            selected={content.emotion ?? []}
                            onChange={(emotion) =>
                                onChange({
                                    ...content,
                                    emotion: emotion as ChiefComplaintContent['emotion'],
                                })
                            }
                            variant="mobile-dialog"
                            placeholder=""
                            groupLabel="Select emotion(s)"
                            searchPlaceholder="Search emotions"
                            notFoundMessage="No emotions found"
                            className="w-full rounded-none border-x-0 border-t-0"
                        />
                    </div>
                    <span className="text-base leading-6">about</span>
                </div>
                <NoteTextarea
                    value={content.about}
                    onChange={(about) => onChange({ ...content, about })}
                    rows={1}
                    variant="inline"
                    className="text-base rounded-none border-x-0 border-t-0 py-1"
                />
            </div>

            <div className="flex flex-col gap-3">
                <p className="text-base text-foreground">Client stated...</p>
                <div
                    className={cn(
                        'relative',
                        'before:absolute before:left-3 before:top-2 before:text-base',
                        'before:pointer-events-none before:select-none',
                        "before:content-['“']",
                    )}
                >
                    <NoteTextarea
                        value={content.directQuote ?? ''}
                        onChange={(directQuote) => onChange({ ...content, directQuote })}
                        rows={2}
                        className="text-base min-h-16 indent-1"
                    />
                </div>
                <p className="text-sm text-muted-foreground">Direct quote</p>
            </div>

            <FieldSection
                label="Discussed thoughts, actions, and feelings related to:"
                className="gap-5"
            >
                <ToggleableViewCheckboxList
                    options={DISCUSSED_TOPIC_OPTIONS}
                    values={content.relatedTo ?? []}
                    onValuesChange={(relatedTo) => onChange({ ...content, relatedTo })}
                    otherValue={content.relatedToOther}
                    onOtherValueChange={(relatedToOther) =>
                        onChange({ ...content, relatedToOther })
                    }
                />
            </FieldSection>
        </FieldSection>
    );
}
